/**
 * The app shell: one layout written once, wrapped around every product route.
 * Marketing, auth and graduation deliberately render outside it.
 *
 * Also the auth guard: every screen behind this layout requires a session.
 * The demo apparatus (VITE_API_MODE=mock) bypasses it entirely, since design
 * review has no real account to sign into.
 */

import { ConsentNotice } from '../patterns/ConsentNotice';
import { Navigate, Outlet } from 'react-router';
import { useSessionStore } from '../../lib/auth/session';
import { useAuthInit } from '../../lib/auth/use-auth-init';
import { ToastHost } from '../primitives/Toast';
import { AlphaWelcome } from '../patterns/AlphaWelcome';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { useResolveProject } from '../../lib/hooks/use-resolve-project';
import { t } from '../../lib/i18n/t';
import { ImpersonationBanner } from './ImpersonationBanner';
import { Sidebar } from './Sidebar';
import { StagingBanner } from './StagingBanner';
import { TopBar } from './TopBar';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

export function AppShell() {
  const slug = useProjectSlug();
  const status = useSessionStore((s) => s.status);
  useAuthInit();
  // A staging environment is not in the account's project list by design, so
  // without this the chrome cannot name the environment somebody is looking at.
  useResolveProject(slug);

  if (!USE_MOCK && status === 'signed-out') {
    return <Navigate to="/sign-in" replace />;
  }
  if (!USE_MOCK && status === 'loading') {
    return null;
  }

  return (
    <>
      {/* ONE sticky stack, not two elements each sticking to top: 0. Two
          siblings that both stick there overlap the moment the page scrolls,
          and the higher one wins: this banner covered the impersonation
          strip's Exit link, which is how an admin stops viewing as somebody
          else. The container sticks; the banners inside it are ordinary flow.
          Environment first, because whether an action is safe at all is a
          bigger fact than whose data it would land on. */}
      <div className="shell-banners">
        <StagingBanner slug={slug} />
        <ImpersonationBanner />
      </div>
      <div className="shell">
        <a className="skip-link" href="#content">
          {t('Skip to content')}
        </a>
        <Sidebar slug={slug} />
        <TopBar slug={slug} />
        <main className="content" id="content">
          <div className="content__inner">
            <Outlet />
          </div>
        </main>
        {/* One host for the whole shell. Every product screen that confirms
            something calls showToast, and without a host mounted above the
            routes those confirmations went nowhere. */}
        <ToastHost />
        <ConsentNotice />
        {/* First sign in only. Mounted here rather than on /projects so it
            greets a person on whatever screen they land, and the tour waits
            for it either way. */}
        <AlphaWelcome />
      </div>
    </>
  );
}
